import type { ReactNode } from "react";
import { createContext, createElement, useEffect, useState } from "react";
import type Cookies from "universal-cookie";

import type {
  AnjouanLicensingData,
  AuthFeatureData,
  BreadcrumbsStaticTranslations,
} from "~/core";
import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import type { SiteSdk } from "~/core/site-sdk";
import type { CfProxyData } from "~/server/extract-cf-headers";
import type { FlagsClient } from "~/shared/utils/feature-flags/core";
import type { LocaleAlternate } from "~/shared/utils/locale-navigation";

const LOGGED_OUT_PLAYER_ID = "-1";
const LOGGED_OUT_PLAYER_ID_STORAGE_KEY = "jpLoggedOutPlayerID";

const getStoredPlayerID = (playerID?: string): string | undefined => {
  if (typeof playerID !== "undefined") {
    return playerID;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  return window.sessionStorage.getItem(LOGGED_OUT_PLAYER_ID_STORAGE_KEY) ===
    LOGGED_OUT_PLAYER_ID
    ? LOGGED_OUT_PLAYER_ID
    : undefined;
};

export type ClientContextBaseData = {
  affiliate?: string;
  featureFlags: FlagsClient;
  authToken?: string;
  loginToken?: string;
  cookies: Cookies;
  breadcrumbsStaticTranslations: BreadcrumbsStaticTranslations;
  playerID?: string;
  amount?: string;
  paymentMethod?: string;
  errorMessage?: string;
  statusCode?: string;
  transactionId?: string;
  windowType?: string;
  authFeatures: AuthFeatureData;
  cfProxyData: CfProxyData;
  language: string;
  siteSdk: SiteSdk;
  availableLanguages: Array<string>;
  languages: GetSettingsQuerySdk["languages"];
  hreflangAlternates?: Array<LocaleAlternate>;
  isNewUser: boolean;
  anjouanLicensingData?: AnjouanLicensingData;
};

export type ClientContextData = ClientContextBaseData & {
  setPlayerID: (playerID: string | undefined) => void;
};

export const ClientContext = createContext<null | ClientContextData>(null);

export const ClientContextProvider = ({
  children,
  value,
}: {
  children: ReactNode;
  value: ClientContextBaseData | null;
}): ReactNode => {
  const [playerID, setPlayerIDState] = useState(() =>
    getStoredPlayerID(value?.playerID),
  );

  const setPlayerID = (nextPlayerID: string | undefined): void => {
    setPlayerIDState(nextPlayerID);

    if (typeof window === "undefined") {
      return;
    }

    if (nextPlayerID === LOGGED_OUT_PLAYER_ID) {
      window.sessionStorage.setItem(
        LOGGED_OUT_PLAYER_ID_STORAGE_KEY,
        LOGGED_OUT_PLAYER_ID,
      );
      return;
    }

    window.sessionStorage.removeItem(LOGGED_OUT_PLAYER_ID_STORAGE_KEY);
  };

  useEffect(() => {
    if (value?.playerID && typeof window !== "undefined") {
      window.sessionStorage.removeItem(LOGGED_OUT_PLAYER_ID_STORAGE_KEY);
    }

    setPlayerIDState(getStoredPlayerID(value?.playerID));
  }, [value?.playerID]);

  const contextValue = value
    ? {
        ...value,
        playerID,
        setPlayerID,
      }
    : null;

  return createElement(
    ClientContext.Provider,
    { value: contextValue },
    children,
  );
};
